import {useState, useMemo} from 'react';
import {SVG} from '../../../../../constants/assetLists';
import {Checkbox} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

type Permission = {
  _id: string;
  name: string;
  displayName: {
    en: string;
    he: string;
  };
  description: string;
};

type GroupNode = {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  parent: string | null;
  permissions: Permission[];
  children: GroupNode[];
  clients?: any[];
};

type Props = {
  allGroupedPermissions: { data: GroupNode[] } | undefined;
  selectedPermissions: string[];
  onPermissionsChange: (permissions: string[]) => void;
  byPermName?: boolean;
};

const ALL_PERMISSIONS_ID = '691eca4365e5ec6f4985460e';
const MOBILE_ONLY_GROUP_ID = '691ecaea65e5ec6f49854637';
const SPECIAL_PARENT_GROUP_ID = '69158064ee1c33314eaad685';

function collectPermissionIds(group: GroupNode): string[] {
  const perms: string[] = group.permissions.map(p => p._id);
  if (group.children?.length) {
    for (const child of group.children) {
      perms.push(...collectPermissionIds(child));
    }
  }
  return perms;
}

function filterOutClientGroups(groups: GroupNode[]): GroupNode[] {
  return groups
    .filter(group => !group.clients?.length)
    .map(group => ({
      ...group,
      children: group.children ? filterOutClientGroups(group.children) : [],
    }));
}

interface PermissionGroup {
  _id: string;
  name: string;
  permissions?: Permission[];
  children?: PermissionGroup[];
}

function getAllPermissions(groups: PermissionGroup[]): Permission[] {
  return groups.flatMap((group) => [
    ...(group.permissions ?? []),
    ...getAllPermissions(group.children ?? []),
  ]);
}
function UserPermissionList({
  allGroupedPermissions,
  selectedPermissions,
  onPermissionsChange,
  byPermName = false,
}: Props) {
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());

  const filteredData = useMemo(() => {
    if (!allGroupedPermissions?.data) return [];
    return filterOutClientGroups(allGroupedPermissions.data);
  }, [allGroupedPermissions]);

  const permissionMaps = useMemo(() => {
    const permissions = getAllPermissions(filteredData);

    return {
      nameToId: new Map(permissions.map((p) => [p.name, p._id])),
      idToName: new Map(permissions.map((p) => [p._id, p.name])),
    };
  }, [filteredData]);

  const allCheckedIds = useMemo(() => {
    if (!byPermName) {
      return new Set(selectedPermissions);
    }

    return new Set(
      selectedPermissions
        .map((name) => permissionMaps.nameToId.get(name))
        .filter(Boolean) as string[],
    );
  }, [selectedPermissions, byPermName, permissionMaps]);

  const emitPermissions = (permissionIds: Set<string>) => {
    if (!byPermName) {
      onPermissionsChange(Array.from(permissionIds));
      return;
    }

    const permissionNames = Array.from(permissionIds)
      .map((id) => permissionMaps.idToName.get(id))
      .filter(Boolean) as string[];

    onPermissionsChange(permissionNames);
  };

  const collectMobileOnlyPermissionIds = (): string[] => {
    const rootGroups = allGroupedPermissions?.data ?? [];
    for (const root of rootGroups) {
      if (root.children?.length) {
        const mobileGroup = root.children.find(
          g => g._id === MOBILE_ONLY_GROUP_ID,
        );
        if (mobileGroup) {
          return collectPermissionIds(mobileGroup);
        }
      }
    }
    return [];
  };

  const toggleExpand = (id: string) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const togglePermission = (permId: string) => {
    const current = new Set(allCheckedIds);
    const isAllPermissions = permId === ALL_PERMISSIONS_ID;
    const mobileOnlyPermIds = collectMobileOnlyPermissionIds();
    const isMobileOnlyPermission = mobileOnlyPermIds.includes(permId);

    if (isAllPermissions) {
      const willSelect = !current.has(permId);
      if (willSelect) {
        mobileOnlyPermIds.forEach(id => current.delete(id));
        current.add(permId);
      } else {
        current.delete(permId);
      }
    } else if (isMobileOnlyPermission) {
      const willSelect = !current.has(permId);
      if (willSelect) {
        current.delete(ALL_PERMISSIONS_ID);
        current.add(permId);
      } else {
        current.delete(permId);
      }
    } else {
      if (current.has(permId)) current.delete(permId);
      else current.add(permId);
    }

    emitPermissions(current);
  };

  const toggleGroup = (group: GroupNode) => {
    const isSpecialParent = group._id === SPECIAL_PARENT_GROUP_ID;
    const isMobileOnlyGroup = group.name === 'Mobile only user';

    if (isSpecialParent && allCheckedIds.has(ALL_PERMISSIONS_ID)) {
      const current = new Set(allCheckedIds);
      current.delete(ALL_PERMISSIONS_ID);
      const mobileOnlyPermIds = collectMobileOnlyPermissionIds();
      mobileOnlyPermIds.forEach(id => current.delete(id));
      emitPermissions(current);
      return;
    }

    const groupPermIds = isSpecialParent
      ? collectMobileOnlyPermissionIds()
      : collectPermissionIds(group);

    if (groupPermIds.length === 0) return;

    const current = new Set(allCheckedIds);
    const allSelected = groupPermIds.every(id => current.has(id));

    if (allSelected) {
      groupPermIds.forEach(id => current.delete(id));
    } else {
      if (isMobileOnlyGroup) {
        current.delete(ALL_PERMISSIONS_ID);
      }
      groupPermIds.forEach(id => current.add(id));
    }

    emitPermissions(current);
  };

  const renderGroup = (group: GroupNode, depth: number) => {
    const expanded = expandedSet.has(group._id);
    const isSpecialParent = group._id === SPECIAL_PARENT_GROUP_ID;

    const groupPermIds = isSpecialParent
      ? collectMobileOnlyPermissionIds()
      : collectPermissionIds(group);

    const hasItems =
      groupPermIds.length > 0 ||
      (group.children?.length ?? 0) > 0 ||
      (group.permissions?.length ?? 0) > 0;

    const showRadio = group.name === 'Mobile only user';

    const allPermissionsSelected = allCheckedIds.has(ALL_PERMISSIONS_ID);

    const allChecked =
      groupPermIds.length > 0 &&
      groupPermIds.every(id => allCheckedIds.has(id));
    const someChecked =
      groupPermIds.some(id => allCheckedIds.has(id)) && !allChecked;

    const uiChecked = isSpecialParent
      ? allPermissionsSelected || allChecked
      : allChecked;

    const uiIndeterminate = isSpecialParent
      ? !allPermissionsSelected && someChecked
      : someChecked;

    const anyPermInGroupSelected =
      groupPermIds.length > 0 && groupPermIds.some(id => allCheckedIds.has(id));
    const disabledRadio = showRadio && !anyPermInGroupSelected;

    const paddingStart = 16 + depth * 32;

    return (
      <div key={group._id}>
        <div
          className="w-full flex items-center py-2 border-b border-gray80"
          style={{paddingInlineStart: paddingStart, paddingInlineEnd: 8}}>
          <div className="flex items-center gap-1">
            {hasItems ? (
              <button
                type="button"
                className="w-[16px] h-[16px] flex items-center justify-center"
                onClick={() => toggleExpand(group._id)}>
                <img
                  src={expanded ? SVG.expandIcon : SVG.lessThanIcon}
                  alt="caret"
                  className="w-[12px] h-[12px] object-contain"
                />
              </button>
            ) : (
              <div className="w-[16px]" />
            )}

            <Checkbox
              color="secondary"
              size="medium"
              sx={{p: 0}}
              checked={uiChecked}
              indeterminate={uiIndeterminate}
              icon={showRadio ? <RadioButtonUncheckedIcon /> : undefined}
              checkedIcon={showRadio ? <RadioButtonCheckedIcon /> : undefined}
              onChange={() => toggleGroup(group)}
            />

            <span className="text-black">
              {group.displayName || group.name}
            </span>
          </div>
        </div>

        {expanded && (
          <div>
            {group.permissions?.map(perm => {
              const checked = allCheckedIds.has(perm._id) || allCheckedIds.has(perm.name);
              const permShowRadio = perm.name === 'all_permissions';
              const permDisabled = disabledRadio && !permShowRadio;

              return (
                <div
                  key={perm._id}
                  className="w-full flex items-center py-2 border-b border-gray80"
                  style={{
                    paddingInlineStart: paddingStart + 32,
                    paddingInlineEnd: 8,
                  }}>
                  <div className="flex items-center gap-1">
                    <Checkbox
                      color="secondary"
                      size="medium"
                      sx={{p: 0}}
                      checked={checked}
                      disabled={permDisabled}
                      onChange={() => togglePermission(perm._id)}
                      icon={
                        permShowRadio ? <RadioButtonUncheckedIcon /> : undefined
                      }
                      checkedIcon={
                        permShowRadio ? <RadioButtonCheckedIcon /> : undefined
                      }
                    />
                    <span className={permDisabled ? 'text-gray' : 'text-black'}>
                      {perm.displayName?.he ||
                        perm.displayName?.en ||
                        perm.name}
                    </span>
                  </div>
                </div>
              );
            })}

            {group.children?.map(child => renderGroup(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 py-2 px-3 border border-gray70 rounded-2xl">
      <p className="m-0">
        כדי להוסיף הרשאות למשתמש זה יש לבחור את האפשרויות הרצויות
      </p>

      <p className="m-0 font-bold">
        רשימת הרשאות ({selectedPermissions.length} נבחרו)
      </p>

      <div className="flex flex-col border border-gray80">
        {filteredData.map(rootGroup => renderGroup(rootGroup, 0))}
      </div>
    </div>
  );
}

export default UserPermissionList;
